<script setup lang="ts">
import { ClockIcon } from '@heroicons/vue/outline';
import { ArrowSmDownIcon, ArrowSmRightIcon } from '@heroicons/vue/solid';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import humanizeDuration from 'humanize-duration';
import type { PropType } from 'vue';
import { getApiRoot } from '~/utilities/api';
import { formatXchPrice } from '~/utilities/chia-display-utils';

const props = defineProps({
  offer: { type: Object as PropType<any>, required: true },
  nft: { type: Object as PropType<any>, required: true },
});
const { offer, nft } = toRefs(props);

const offeredThings = Object.keys(offer.value.offered || {});
const requestedThings = Object.keys(offer.value.requested || {});

const nfts = ref<{ [assetId: string]: any }>({});
for (const assetId of [...requestedThings, ...offeredThings]) {
  if (assetId.startsWith('nft')) {
    nfts.value[assetId] = await $fetch<any>(`${getApiRoot()}/nfts/${assetId}`);
  }
}

const expirationDiff = computed(() =>
  offer.value?.expiration_date ? differenceInMilliseconds(parseISO(offer.value.expiration_date), new Date()) : undefined
);
const formatDuration = (diff: number) => {
  return humanizeDuration(diff, {
    largest: 2,
    delimiter: ' and ',
    maxDecimalPoints: 0,
    // language: locale.value.replace('zh', 'zh_CN') || 'en',
  });
};
</script>
<template>
  <div class="mb-8 dark:text-white lg:mb-16">
    <div class="mb-8 text-sm font-medium text-neutral-700 dark:text-neutral-200 lg:mb-16 lg:mt-4">
      <div v-if="expirationDiff > 0" class="inline-flex items-center gap-0.5">
        <ClockIcon class="h-5 w-5" />
        Will expire in {{ formatDuration(expirationDiff) }}.
      </div>
      <div v-if="expirationDiff < 0" class="inline-flex items-center gap-0.5">
        <ClockIcon class="h-5 w-5" />
        Expired {{ formatDuration(expirationDiff) }} ago.
      </div>
    </div>
    <div class="grid gap-4 lg:grid-cols-[1fr_64px_1fr] lg:grid-rows-[auto_1fr] lg:gap-x-12">
      <div class="row-end-1 text-2xl">{{ $t('offers.offering') }}</div>
      <div class="row-end-1 hidden lg:block" />

      <div class="flex flex-col">
        <div v-if="offeredThings.length === 1 && offeredThings[0].startsWith('nft')">
          <NftCard :nft="nfts[offeredThings[0]]" />
        </div>
        <div v-else class="flex flex-grow flex-col items-center justify-center gap-4">
          <template v-for="offeredThing in offeredThings">
            <span v-if="offeredThing === 'xch'" class="text-4xl font-medium">
              {{ formatXchPrice(offer.xch_price) }}
            </span>
            <NuxtLink
              :to="`/nfts/${offeredThing}`"
              v-else-if="offeredThing.startsWith('nft')"
              class="mb-1 inline-flex items-center gap-1 text-4xl font-medium"
            >
              <img
                v-if="nfts[offeredThing]?.data?.thumbnail_uri"
                :src="nfts[offeredThing]?.data?.thumbnail_uri"
                loading="lazy"
                class="h-10 w-10 flex-shrink-0 rounded-md border object-cover object-center"
              />
              <span class="truncate">
                {{ nfts[offeredThing]?.data?.metadata_json?.name || offeredThing }}
              </span>
            </NuxtLink>
            <NuxtLink
              v-else
              target="_blank"
              :to="`https://dexie.space/assets/${offeredThing}`"
              class="mb-1 inline-flex items-center gap-1 text-4xl font-medium"
            >
              <img class="h-10 w-10 rounded-full" :src="`https://icons.dexie.space/${offeredThing}.webp`" />{{
                offer.requested[offeredThing]
              }}
            </NuxtLink>
          </template>
          <span v-if="nft.royalty_percentage" class="text-xs text-neutral-600 dark:text-neutral-300">
            {{ $t('offers.plus') }} {{ nft.royalty_percentage / 100 }}% {{ $t('offers.creator_royalties') }}
          </span>
        </div>
      </div>
      <div class="flex flex-grow items-center justify-center">
        <ArrowSmRightIcon class="hidden h-12 w-12 text-neutral-300 dark:text-neutral-600 lg:block" />
        <ArrowSmDownIcon class="h-12 w-12 text-neutral-300 dark:text-neutral-600 lg:hidden" />
      </div>
      <div class="text-2xl lg:row-end-1">{{ $t('offers.requesting') }}</div>
      <div class="flex flex-col">
        <div v-if="requestedThings.length === 1 && requestedThings[0].startsWith('nft')">
          <NftCard :nft="nfts[requestedThings[0]]" />
        </div>
        <div v-else class="flex flex-grow flex-col items-center justify-center gap-4">
          <template v-for="requestedThing in requestedThings">
            <span v-if="requestedThing === 'xch'" class="text-4xl font-medium">
              {{ formatXchPrice(offer.xch_price) }}
            </span>
            <NuxtLink
              :to="`/nfts/${requestedThing}`"
              v-else-if="requestedThing.startsWith('nft')"
              class="mb-1 inline-flex items-center gap-1 text-4xl font-medium"
            >
              <img
                v-if="nfts[requestedThing]?.data?.thumbnail_uri"
                :src="nfts[requestedThing]?.data?.thumbnail_uri"
                loading="lazy"
                class="h-10 w-10 flex-shrink-0 rounded-md border object-cover object-center"
              />
              <span class="truncate">
                {{ nfts[requestedThing]?.data?.metadata_json?.name || requestedThing }}
              </span>
            </NuxtLink>
            <NuxtLink
              v-else
              target="_blank"
              :to="`https://dexie.space/assets/${requestedThing}`"
              class="mb-1 inline-flex items-center gap-1 text-4xl font-medium"
            >
              <img class="h-10 w-10 rounded-full" :src="`https://icons.dexie.space/${requestedThing}.webp`" />{{
                offer.requested[requestedThing]
              }}
            </NuxtLink>
          </template>
          <span v-if="nft.royalty_percentage" class="text-xs text-neutral-600 dark:text-neutral-300">
            {{ $t('offers.plus') }} {{ nft.royalty_percentage / 100 }}% {{ $t('offers.creator_royalties') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
